/**
 * 把 AI 生成的作业单结构渲染成一份可直接打印的自包含 HTML。
 *
 * 刻意不走 paper_html 那条"让 LLM 排版"的路:作业单的版式是确定的,
 * 用模板拼比再调一次模型又快又稳,也不会有排版漂移。产物是 .html ——
 * supernova 解析器认这个后缀,所以入库后可检索(不像 ppt 只能预览)。
 *
 * 落盘走的是 documents 那条老路(assignments/{id} 文件夹),和老师手工
 * 上传的作业单进同一个地方,预览/入库/删除全都复用现成机制。
 */

import { mathToHtml } from "../../lib/mathText";
import { FIGURE_CSS, figureHtml, safeSvg } from "../../lib/safeSvg";

export type HomeworkItem = {
  seq: number;
  stem: string;
  item_type: string;
  options?: { key: string; text: string }[];
  answer: string;
  knowledge_point?: string | null;
  /** 配图:agent 的图形说明 + 后端画好的内联 SVG(2026-09-20)。 */
  figure?: string | null;
  figure_svg?: string | null;
};

export type HomeworkSheet = {
  title: string;
  instructions?: string | null;
  items: HomeworkItem[];
};

function esc(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** 题干保留换行,其余按纯文本转义 —— 不引 Markdown 渲染器,作业单
 *  的题干本来就该是一两行朴素文字,引进来反而多一层跑偏的可能。
 *  例外是数学公式:理科助教一律写 `$…$` LaTeX(聊天里就是这么渲染的),
 *  这里交给 mathToHtml 出成 MathML,自包含、打印不依赖字体(2026-09-14
 *  之前原样打出 "$(-3) + (-7)$",老师看到的就是截图里那样)。 */
function stemHtml(s: string): string {
  return mathToHtml(s, { breaks: true });
}

export function renderHomeworkHtml(
  sheet: HomeworkSheet,
  meta: { className?: string; assignedDate?: string },
): string {
  const items = [...sheet.items].sort((a, b) => a.seq - b.seq);

  let figNo = 0;
  const body = items
    .map((it) => {
      const svg = safeSvg(it.figure_svg);
      const fig = svg ? figureHtml(svg, `图${++figNo}`) : "";
      const opts =
        it.options && it.options.length > 0
          ? `<ol class="opts">${it.options
              .map((o) => `<li><b>${esc(o.key)}.</b> ${mathToHtml(o.text)}</li>`)
              .join("")}</ol>`
          : "";
      // 简答/写作留出手写空间,选择/填空不留 —— 打印出来才像张作业单。
      const roomy = ["short_answer", "writing", "translation"].includes(it.item_type);
      const space = roomy ? '<div class="space"></div>' : "";
      return `<li class="q"><div class="stem">${stemHtml(it.stem)}</div>${fig}${opts}${space}</li>`;
    })
    .join("");

  const key = items
    .map(
      (it) =>
        `<li><b>${it.seq}.</b> ${mathToHtml(it.answer, { breaks: true })}${
          it.knowledge_point ? ` <span class="kp">(${esc(it.knowledge_point)})</span>` : ""
        }</li>`,
    )
    .join("");

  const subtitle = [meta.className, meta.assignedDate].filter(Boolean).join(" · ");

  return `<!DOCTYPE html><html lang="zh-CN"><head><meta charset="utf-8">
<title>${esc(sheet.title)}</title>
<style>
  body { font-family: -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif;
         max-width: 760px; margin: 0 auto; padding: 32px 24px; color: #1e293b; line-height: 1.7; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .sub { color: #64748b; font-size: 13px; margin-bottom: 16px; }
  .instructions { background: #f8fafc; border-left: 3px solid #38bdf8;
                  padding: 8px 12px; font-size: 14px; margin-bottom: 20px; }
  .name-row { display: flex; gap: 32px; font-size: 14px; color: #475569;
              border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 20px; }
  ol.qs { padding-left: 22px; }
  li.q { margin-bottom: 18px; }
  .stem { font-size: 15px; }
  math { font-size: 1.05em; }
  ol.opts { list-style: none; padding-left: 8px; margin: 6px 0 0; font-size: 14px; }
  ol.opts li { margin: 2px 0; }
  .space { border-bottom: 1px solid #cbd5e1; height: 44px; margin-top: 8px; }
  .key { margin-top: 36px; padding-top: 16px; border-top: 2px dashed #cbd5e1; }
  .key h2 { font-size: 16px; margin: 0 0 8px; }
  .key ol { list-style: none; padding-left: 0; font-size: 14px; }
  .key li { margin: 4px 0; }
  .kp { color: #94a3b8; font-size: 12px; }
  li.q { page-break-inside: avoid; break-inside: avoid; }${FIGURE_CSS}
  @media print { .key { page-break-before: always; } }
</style></head><body>
<h1>${esc(sheet.title)}</h1>
${subtitle ? `<div class="sub">${esc(subtitle)}</div>` : ""}
${sheet.instructions ? `<div class="instructions">${stemHtml(sheet.instructions)}</div>` : ""}
<div class="name-row"><span>姓名：____________</span><span>班级：____________</span><span>得分：________</span></div>
<ol class="qs">${body}</ol>
<div class="key"><h2>参考答案</h2><ol>${key}</ol></div>
</body></html>`;
}


/** 记名版(博颂 2026-09-22「作业需要像考试一样可以实名制」):每个学生一份,
 *  卷头已填姓名 / 学号 / 班级,份与份之间分页。不带参考答案。 */
export function renderHomeworkNamedHtml(
  sheet: HomeworkSheet,
  meta: { className?: string; assignedDate?: string },
  students: { name_zh: string; name_en?: string | null; student_code?: string | null }[],
): string {
  const single = renderHomeworkHtml(sheet, meta);
  // 拆出 <body> 内容,去掉答案区,按学生复制
  const bodyMatch = /<body>([\s\S]*)<\/body>/.exec(single);
  const head = single.slice(0, single.indexOf("<body>"));
  let inner: string = bodyMatch?.[1] ?? single;
  inner = inner.replace(/<div class="key">[\s\S]*?<\/div>\s*$/, "");
  const copies = students.map((st) => {
    const nameRow = `<div class="name-row"><span>姓名：${esc(st.name_zh)}${st.name_en ? ` / ${esc(st.name_en)}` : ""}</span><span>学号：${esc(st.student_code ?? "")}</span><span>班级：${esc(meta.className ?? "")}</span><span>得分：________</span></div>`;
    return `<section class="copy">${inner.replace(/<div class="name-row">[\s\S]*?<\/div>/, nameRow)}</section>`;
  }).join("\n");
  const css = `<style>.copy{page-break-after:always;break-after:page}.copy:last-child{page-break-after:auto;break-after:auto}</style>`;
  return `${head.replace("</head>", css + "</head>")}<body>${copies}</body></html>`;
}
